import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowRight,
  BadgeCheck,
  Menu,
  QrCode,
  ShieldCheck,
  Smartphone,
  Store,
} from "lucide-react";
import api from "@/lib/api";

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  userPhone: "",
  password: "",
  passwordConfirm: "",
  businessName: "",
  businessAddress: "",
  businessPhone: "",
  businessEmail: "",
  businessSubDomain: "",
  businessSector: "",
};

const FIELD_LABELS = {
  firstName: "Ad",
  lastName: "Soyad",
  email: "E-posta",
  userPhone: "Telefon",
  password: "Şifre",
  passwordConfirm: "Şifre Tekrar",
  businessName: "İşletme Adı",
  businessAddress: "Adres",
  businessPhone: "İşletme Telefonu",
  businessEmail: "İşletme E-posta",
  businessSubDomain: "Subdomain",
  businessSector: "İşletme Sektörü",
};


const BACKEND_PROPERTY_TO_FORM_FIELD = {
  Username: "email",
  Email: "email",
  UserPhone: "userPhone",
  Password: "password",
  FirstName: "firstName",
  LastName: "lastName",
  "Business.Name": "businessName",
  "Business.Address": "businessAddress",
  "Business.Phone": "businessPhone",
  "Business.Email": "businessEmail",
  "Business.SubDomain": "businessSubDomain",
  "Business.TypeConstantValue": "businessSector",
};

const BACKEND_MESSAGE_MAP = {
  "'Business Phone' must not be empty.": "İşletme telefonu boş bırakılamaz.",
  "'Business Name' must not be empty.": "İşletme adı boş bırakılamaz.",
  "'Business Sub Domain' must not be empty.": "Subdomain boş bırakılamaz.",
  "'Business Email' must not be empty.": "İşletme e-posta alanı boş bırakılamaz.",
  "'Email' must not be empty.": "E-posta boş bırakılamaz.",
  "'Password' must not be empty.": "Şifre boş bırakılamaz.",
  "Username sadece küçük harf, rakam, '.' ve '_' içerebilir.":
  "Kullanıcı adı sadece küçük harf, rakam, '.' ve '_' içerebilir.",
  "Telefon numarası 05XXXXXXXXX formatında olmalı":
  "Telefon numarası 05XXXXXXXXX formatında olmalı.",
};

function getInputClass(hasError) {
  return hasError
    ? "border-red-500 focus-visible:ring-red-500"
    : "";
}

function translateBackendMessage(item) {
  const rawMessage = item?.ErrorMessage || "";

  if (BACKEND_MESSAGE_MAP[rawMessage]) {
    return BACKEND_MESSAGE_MAP[rawMessage];
  }

  if (
    item?.PropertyName === "Business.Phone" &&
    rawMessage.toLowerCase().includes("must not be empty")
  ) {
    return "İşletme telefonu boş bırakılamaz.";
  }

  if (
    item?.PropertyName === "Business.Name" &&
    rawMessage.toLowerCase().includes("must not be empty")
  ) {
    return "İşletme adı boş bırakılamaz.";
  }

  if (
    item?.PropertyName === "Business.SubDomain" &&
    rawMessage.toLowerCase().includes("must not be empty")
  ) {
    return "Subdomain boş bırakılamaz.";
  }

  if (
  item?.PropertyName === "Username" &&
  rawMessage.toLowerCase().includes("must not be empty")
  ) {
    return "E-posta boş bırakılamaz.";
  }

  if (
    item?.PropertyName === "Email" &&
    rawMessage.toLowerCase().includes("must not be empty")
  ) {
    return "E-posta boş bırakılamaz.";
  }

  if (
    item?.PropertyName === "Password" &&
    rawMessage.toLowerCase().includes("must not be empty")
  ) {
    return "Şifre boş bırakılamaz.";
  }

  return rawMessage || "Bir doğrulama hatası oluştu.";
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [businessSectors, setBusinessSectors] = useState([]);
  const [constantsLoading, setConstantsLoading] = useState(false);

  useEffect(() => {
  const loadBusinessSectors = async () => {
    setConstantsLoading(true);

    try {
      const res = await api.getPublicConstants({
        type: "BUSINESS_SECTOR",
        pageSize: 1000,
      });

      setBusinessSectors(res?.items || []);
    } catch (error) {
      console.error("Sektörler yüklenemedi:", error);
      toast.error("İşletme sektörleri yüklenemedi.");
    } finally {
      setConstantsLoading(false);
    }
    };

    loadBusinessSectors();
  }, []);

  const fieldHasError = useMemo(
    () => (field) => Boolean(errors[field]),
    [errors]
  );

  const clearFieldError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;

    setForm((prev) => ({ ...prev, [field]: value }));
    clearFieldError(field);

    if (field === "password" || field === "passwordConfirm") {
      clearFieldError("password");
      clearFieldError("passwordConfirm");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.businessName.trim()) {
      newErrors.businessName = "Lütfen işletme adını girin.";
    }

    if (!form.businessSector) {
      newErrors.businessSector = "Lütfen işletme sektörünü seçin.";
    }

    if (!form.email.trim()) {
      newErrors.email = "Lütfen kullanıcı e-posta adresini girin.";
    }

    if (!form.password.trim()) {
      newErrors.password = "Lütfen şifre girin.";
    }

    if (!form.passwordConfirm.trim()) {
      newErrors.passwordConfirm = "Lütfen şifre tekrarını girin.";
    }

    if (
      form.password.trim() &&
      form.passwordConfirm.trim() &&
      form.password !== form.passwordConfirm
    ) {
      newErrors.passwordConfirm = "Şifreler uyuşmuyor.";
    }

    const subdomainValue = form.businessSubDomain.trim().toLowerCase();

    if (!subdomainValue) {
      newErrors.businessSubDomain =
        "Lütfen subdomain girin. Bu alan QR kod ve menü linki için gereklidir.";
    } else if (!/^[a-z0-9-]+$/.test(subdomainValue)) {
      newErrors.businessSubDomain =
        "Subdomain sadece küçük harf, rakam ve tire içerebilir.";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstError = Object.values(newErrors)[0];
      toast.error(firstError);
      return false;
    }

    return true;
  };

  const applyBackendFailures = (failures = []) => {
    const nextErrors = {};
    const shownMessages = new Set();

    failures.forEach((item) => {
      const field = BACKEND_PROPERTY_TO_FORM_FIELD[item?.PropertyName];
      const translatedMessage = translateBackendMessage(item);

      if (field && !nextErrors[field]) {
        nextErrors[field] = translatedMessage;
      }

      if (!shownMessages.has(translatedMessage)) {
        toast.error(translatedMessage);
        shownMessages.add(translatedMessage);
      }
    });

    if (Object.keys(nextErrors).length > 0) {
      setErrors((prev) => ({
        ...prev,
        ...nextErrors,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      setErrors({});

      await api.register({
      email: form.email.trim(),
      password: form.password,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      phone: form.userPhone.trim(),
      businessName: form.businessName.trim(),
      businessAddress: form.businessAddress.trim(),
      businessPhone: form.businessPhone.trim(),
      businessEmail: form.businessEmail.trim(),
      businessSubDomain: form.businessSubDomain.trim().toLowerCase(),
      businessSector: form.businessSector,
    });

      toast.success("Kayıt başarılı! Giriş yapılıyor...");

      await login(form.email.trim(), form.password);
      navigate("/admin");
    } catch (error) {
      console.error("REGISTER ERROR FULL:", error);

      const data = error?.response?.data;

      if (Array.isArray(data?.Failures) && data.Failures.length > 0) {
        applyBackendFailures(data.Failures);
        return;
      }

      if (typeof data === "string" && data.trim()) {
        toast.error(data);
        return;
      }

      if (data?.Detail) {
        toast.error(data.Detail);
        return;
      }

      if (data?.detail) {
        toast.error(data.detail);
        return;
      }

      if (data?.Title) {
        toast.error(data.Title);
        return;
      }

      if (data?.title) {
        toast.error(data.title);
        return;
      }

      toast.error("Kayıt sırasında bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const ErrorText = ({ field }) =>
    errors[field] ? (
      <p className="mt-1 text-xs text-red-600">{errors[field]}</p>
    ) : null;

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#FCFBF9] text-zinc-950">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-zinc-200/70 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex min-w-0 items-center gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[14px] bg-[#EA580C] text-white shadow-[0_10px_30px_rgba(234,88,12,0.22)]">
              <Menu className="h-5 w-5" />
            </div>

            <div className="min-w-0 leading-tight">
              <div className="truncate text-[15px] font-extrabold tracking-[-0.02em] text-zinc-950">
                Web Menü
              </div>
              <div className="mt-0.5 truncate text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
                Megriva teknolojisiyle
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="hidden rounded-full px-4 py-2 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-950 sm:inline-flex"
            >
              Ana Sayfa
            </Link>

            <Link
              to="/login"
              className="inline-flex h-10 items-center justify-center rounded-full border border-zinc-200 bg-white px-5 text-sm font-semibold text-zinc-800 transition hover:bg-zinc-50"
            >
              Giriş Yap
            </Link>
          </div>
        </div>
      </header>

      <div aria-hidden="true" className="h-[72px]" />

      <main className="relative">
        <div
          aria-hidden="true"
          className="absolute left-[-180px] top-[-140px] h-[420px] w-[420px] rounded-full bg-orange-200/35 blur-[110px]"
        />
        <div
          aria-hidden="true"
          className="absolute right-[-180px] top-[200px] h-[460px] w-[460px] rounded-full bg-amber-100/60 blur-[120px]"
        />

        <div className="relative mx-auto grid max-w-7xl items-start lg:grid-cols-[0.78fr_1.22fr]">
          <aside className="hidden min-h-[calc(100vh-72px)] border-r border-zinc-200/70 px-8 py-12 lg:sticky lg:top-0 lg:flex lg:flex-col lg:justify-between xl:px-12 xl:py-14">
            <div className="max-w-md">
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white px-4 py-2 text-xs font-bold text-orange-700 shadow-sm">
                <BadgeCheck className="h-4 w-4" />
                Web Menü hesabınızı oluşturun
              </div>

              <h1 className="mt-7 text-[46px] font-extrabold leading-[1.04] tracking-[-0.045em] text-zinc-950 xl:text-[54px]">
                Dijital vitrininizi{" "}
                <span className="text-[#EA580C]">dakikalar içinde</span>{" "}
                hazırlayın.
              </h1>

              <p className="mt-6 text-sm font-medium leading-7 text-zinc-600">
                Hesap ve işletme bilgilerinizi tamamlayın. Ardından ürünlerinizi,
                hizmetlerinizi ve kampanyalarınızı Web Menü üzerinden yönetmeye
                başlayın.
              </p>

              <div className="mt-8 grid gap-3">
                {[
                  {
                    icon: Store,
                    title: "Her işletmeye uygun",
                    text: "Restoran, mobilya, güzellik, oto, üretim ve daha fazlası.",
                  },
                  {
                    icon: QrCode,
                    title: "Tek QR ve özel bağlantı",
                    text: "Müşterileriniz güncel vitrininize aynı bağlantıdan ulaşır.",
                  },
                  {
                    icon: Smartphone,
                    title: "Web + mobil yönetim",
                    text: "İçeriklerinizi ihtiyacınız olduğunda kolayca güncelleyin.",
                  },
                ].map((item) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={item.title}
                      className="flex gap-3 rounded-[18px] border border-zinc-200/80 bg-white/80 p-4 shadow-[0_10px_30px_rgba(24,24,27,0.04)] backdrop-blur"
                    >
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[13px] bg-orange-50 text-[#EA580C]">
                        <Icon className="h-[18px] w-[18px]" />
                      </div>

                      <div>
                        <p className="text-sm font-extrabold text-zinc-900">
                          {item.title}
                        </p>
                        <p className="mt-1 text-xs font-medium leading-5 text-zinc-500">
                          {item.text}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-10 rounded-[22px] bg-zinc-950 p-5 text-white">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-[13px] bg-white/10 text-orange-300">
                  <ShieldCheck className="h-5 w-5" />
                </div>

                <div>
                  <p className="text-sm font-extrabold">Tek hesap, tek yönetim alanı</p>
                  <p className="mt-1 text-xs font-medium text-zinc-400">
                    Hesabınızı oluşturduktan sonra doğrudan yönetim paneline geçersiniz.
                  </p>
                </div>
              </div>
            </div>
          </aside>

          <section className="px-4 py-9 sm:px-6 sm:py-12 lg:px-10 xl:px-14">
            <div className="mx-auto w-full max-w-[790px]">
              <div className="mb-7 lg:hidden">
                <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white px-3.5 py-2 text-[11px] font-bold text-orange-700 shadow-sm">
                  <BadgeCheck className="h-4 w-4" />
                  Web Menü hesabınızı oluşturun
                </div>
              </div>

              <div className="mb-8">
                <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#EA580C]">
                  Kayıt Ol
                </p>

                <h2
                  className="mt-3 text-3xl font-extrabold tracking-[-0.04em] text-zinc-950 sm:text-[42px]"
                  data-testid="register-heading"
                >
                  İşletmenizi dijitale taşıyın.
                </h2>

                <p className="mt-3 max-w-2xl text-sm font-medium leading-6 text-zinc-500">
                  Hesabınızı ve işletme bilgilerinizi oluşturun. Web Menü
                  adresiniz hazır olduğunda içeriğinizi tek panelden yönetmeye
                  başlayabilirsiniz.
                </p>
              </div>

              <div className="mb-7 grid gap-3 sm:grid-cols-2">
                <div className="flex items-center gap-3 rounded-[18px] border border-orange-200 bg-orange-50/60 p-4">
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[13px] bg-[#EA580C] text-sm font-extrabold text-white">
                    01
                  </div>
                  <div>
                    <p className="text-sm font-extrabold text-zinc-900">
                      Hesap Bilgileri
                    </p>
                    <p className="mt-0.5 text-xs font-medium text-zinc-500">
                      Giriş yapacağınız kullanıcı bilgileri
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 rounded-[18px] border border-zinc-200 bg-white p-4">
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[13px] bg-zinc-100 text-sm font-extrabold text-zinc-600">
                    02
                  </div>
                  <div>
                    <p className="text-sm font-extrabold text-zinc-900">
                      İşletme Bilgileri
                    </p>
                    <p className="mt-0.5 text-xs font-medium text-zinc-500">
                      Dijital vitrininizin temel bilgileri
                    </p>
                  </div>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                className="space-y-5"
                data-testid="register-form"
                noValidate
              >
                <section className="rounded-[26px] border border-zinc-200/80 bg-white p-5 shadow-[0_18px_55px_rgba(24,24,27,0.05)] sm:p-7">
                  <div className="mb-6 flex items-start gap-3 border-b border-zinc-100 pb-5">
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[13px] bg-orange-50 text-xs font-extrabold text-[#EA580C]">
                      01
                    </div>

                    <div>
                      <h3 className="text-base font-extrabold text-zinc-950">
                        Hesap Bilgileri
                      </h3>
                      <p className="mt-1 text-xs font-medium leading-5 text-zinc-500">
                        Web Menü yönetim paneline giriş yapacak kullanıcı bilgileri.
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-5 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-xs font-bold text-zinc-700">
                        {FIELD_LABELS.firstName}
                      </Label>
                      <Input
                        id="firstName"
                        value={form.firstName}
                        onChange={handleChange("firstName")}
                        placeholder="Adınız"
                        autoComplete="given-name"
                        className={`h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200 ${getInputClass(
                          fieldHasError("firstName")
                        )}`}
                      />
                      <ErrorText field="firstName" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-xs font-bold text-zinc-700">
                        {FIELD_LABELS.lastName}
                      </Label>
                      <Input
                        id="lastName"
                        value={form.lastName}
                        onChange={handleChange("lastName")}
                        placeholder="Soyadınız"
                        autoComplete="family-name"
                        className={`h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200 ${getInputClass(
                          fieldHasError("lastName")
                        )}`}
                      />
                      <ErrorText field="lastName" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-xs font-bold text-zinc-700">
                        E-posta
                      </Label>
                      <Input
                        id="email"
                        type="text"
                        value={form.email}
                        onChange={handleChange("email")}
                        placeholder="ornek@mail.com"
                        autoComplete="email"
                        className={`h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200 ${getInputClass(
                          fieldHasError("email")
                        )}`}
                        required
                        data-testid="register-email-input"
                      />
                      <p className="text-[11px] font-medium text-zinc-400">
                        Geçerli bir e-posta adresi girin.
                      </p>
                      <ErrorText field="email" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="userPhone" className="text-xs font-bold text-zinc-700">
                        {FIELD_LABELS.userPhone}
                      </Label>
                      <Input
                        id="userPhone"
                        value={form.userPhone}
                        onChange={handleChange("userPhone")}
                        placeholder="05XXXXXXXXX"
                        autoComplete="tel"
                        className={`h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200 ${getInputClass(
                          fieldHasError("userPhone")
                        )}`}
                      />
                      <ErrorText field="userPhone" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-xs font-bold text-zinc-700">
                        {FIELD_LABELS.password}
                      </Label>
                      <Input
                        id="password"
                        type="password"
                        value={form.password}
                        onChange={handleChange("password")}
                        placeholder="••••••••"
                        autoComplete="new-password"
                        className={`h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200 ${getInputClass(
                          fieldHasError("password")
                        )}`}
                        required
                        data-testid="register-password-input"
                      />
                      <ErrorText field="password" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="passwordConfirm" className="text-xs font-bold text-zinc-700">
                        Şifre (Tekrar)
                      </Label>
                      <Input
                        id="passwordConfirm"
                        type="password"
                        value={form.passwordConfirm}
                        onChange={handleChange("passwordConfirm")}
                        placeholder="••••••••"
                        autoComplete="new-password"
                        className={`h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200 ${getInputClass(
                          fieldHasError("passwordConfirm")
                        )}`}
                        required
                        data-testid="register-password-confirm-input"
                      />
                      <ErrorText field="passwordConfirm" />
                    </div>
                  </div>
                </section>

                <section className="rounded-[26px] border border-zinc-200/80 bg-white p-5 shadow-[0_18px_55px_rgba(24,24,27,0.05)] sm:p-7">
                  <div className="mb-6 flex items-start gap-3 border-b border-zinc-100 pb-5">
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[13px] bg-zinc-950 text-xs font-extrabold text-white">
                      02
                    </div>

                    <div>
                      <h3 className="text-base font-extrabold text-zinc-950">
                        İşletme Bilgileri
                      </h3>
                      <p className="mt-1 text-xs font-medium leading-5 text-zinc-500">
                        Web Menü vitrininizin müşterilere gösterilecek temel bilgileri.
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-5 md:grid-cols-2">
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="businessName" className="text-xs font-bold text-zinc-700">
                        {FIELD_LABELS.businessName}
                      </Label>
                      <Input
                        id="businessName"
                        value={form.businessName}
                        onChange={handleChange("businessName")}
                        placeholder="Örneğin: Arven Concept"
                        autoComplete="organization"
                        className={`h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200 ${getInputClass(
                          fieldHasError("businessName")
                        )}`}
                        required
                        data-testid="register-business-name-input"
                      />
                      <ErrorText field="businessName" />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="businessSector" className="text-xs font-bold text-zinc-700">
                        İşletme Sektörü <span className="text-red-500">*</span>
                      </Label>

                      <select
                        id="businessSector"
                        value={form.businessSector}
                        onChange={(event) => {
                          setForm((prev) => ({
                            ...prev,
                            businessSector: event.target.value,
                          }));
                          clearFieldError("businessSector");
                        }}
                        disabled={constantsLoading}
                        required
                        className={`flex h-12 w-full rounded-[14px] border border-zinc-200 bg-zinc-50/70 px-4 py-2 text-sm text-zinc-900 outline-none transition-colors focus:border-orange-300 focus:ring-2 focus:ring-orange-100 disabled:cursor-not-allowed disabled:opacity-50 ${getInputClass(
                          fieldHasError("businessSector")
                        )}`}
                      >
                        <option value="" disabled>
                          {constantsLoading
                            ? "Sektörler yükleniyor..."
                            : "İşletmenizin sektörünü seçin"}
                        </option>

                        {businessSectors.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.name}
                          </option>
                        ))}
                      </select>

                      <ErrorText field="businessSector" />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="businessAddress" className="text-xs font-bold text-zinc-700">
                        {FIELD_LABELS.businessAddress}
                      </Label>
                      <Input
                        id="businessAddress"
                        value={form.businessAddress}
                        onChange={handleChange("businessAddress")}
                        placeholder="İşletme adresi"
                        autoComplete="street-address"
                        className={`h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200 ${getInputClass(
                          fieldHasError("businessAddress")
                        )}`}
                      />
                      <ErrorText field="businessAddress" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="businessPhone" className="text-xs font-bold text-zinc-700">
                        {FIELD_LABELS.businessPhone}
                      </Label>
                      <Input
                        id="businessPhone"
                        value={form.businessPhone}
                        onChange={handleChange("businessPhone")}
                        placeholder="05XXXXXXXXX"
                        autoComplete="tel"
                        className={`h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200 ${getInputClass(
                          fieldHasError("businessPhone")
                        )}`}
                      />
                      <ErrorText field="businessPhone" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="businessEmail" className="text-xs font-bold text-zinc-700">
                        {FIELD_LABELS.businessEmail}
                      </Label>
                      <Input
                        id="businessEmail"
                        type="email"
                        value={form.businessEmail}
                        onChange={handleChange("businessEmail")}
                        placeholder="info@isletmeniz.com"
                        autoComplete="email"
                        className={`h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200 ${getInputClass(
                          fieldHasError("businessEmail")
                        )}`}
                      />
                      <ErrorText field="businessEmail" />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="businessSubDomain" className="text-xs font-bold text-zinc-700">
                        Web Menü Adresi
                      </Label>

                      <div className="flex min-w-0 items-stretch overflow-hidden rounded-[14px] border border-zinc-200 bg-zinc-50/70 focus-within:border-orange-300 focus-within:ring-2 focus-within:ring-orange-100">
                        <span className="hidden items-center border-r border-zinc-200 bg-white px-4 text-xs font-semibold text-zinc-400 sm:flex">
                          webmenu.megriva.com/
                        </span>
                        <Input
                          id="businessSubDomain"
                          value={form.businessSubDomain}
                          onChange={handleChange("businessSubDomain")}
                          placeholder="isletmeniz"
                          className={`h-12 min-w-0 flex-1 rounded-none border-0 bg-transparent px-4 shadow-none focus-visible:ring-0 ${getInputClass(
                            fieldHasError("businessSubDomain")
                          )}`}
                          required
                        />
                      </div>

                      <p className="text-[11px] font-medium leading-5 text-zinc-400">
                        Bu adres Web Menü bağlantınız ve QR kodunuz için kullanılır.
                        Yalnızca küçük harf, rakam ve tire kullanın.
                      </p>
                      <ErrorText field="businessSubDomain" />
                    </div>
                  </div>
                </section>

                <div className="rounded-[22px] border border-orange-200 bg-orange-50/60 p-4 sm:flex sm:items-center sm:justify-between sm:gap-5">
                  <div>
                    <p className="text-sm font-extrabold text-zinc-900">
                      Hazırsanız Web Menü'nüzü oluşturalım.
                    </p>
                    <p className="mt-1 text-xs font-medium leading-5 text-zinc-500">
                      Kayıt tamamlandığında otomatik olarak yönetim panelinize giriş yapılır.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="mt-4 h-12 w-full shrink-0 rounded-full bg-[#EA580C] px-7 text-sm font-bold text-white shadow-[0_14px_32px_rgba(234,88,12,0.22)] transition hover:bg-[#C2410C] active:scale-[0.99] disabled:opacity-70 sm:mt-0 sm:w-auto"
                    disabled={loading}
                    data-testid="register-submit-button"
                  >
                    {loading ? (
                      "Kaydediliyor..."
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        Hesap Oluştur
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    )}
                  </Button>
                </div>
              </form>

              <p className="mt-7 text-center text-sm font-medium text-zinc-500">
                Zaten bir hesabınız var mı?{" "}
                <Link
                  to="/login"
                  className="font-bold text-[#EA580C] transition hover:text-[#C2410C]"
                  data-testid="register-login-link"
                >
                  Giriş yapın
                </Link>
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
